import type { CSSProperties } from 'react'
import type { LiffTheme } from '@/types'

/**
 * The account's login-page theme as the CSS custom properties
 * `styles/liff-login.css` reads, shared by the lease-code and OTP pages (they
 * each carried a copy of this before).
 *
 * 2026-09-30 (owner): the title alignment, how far the panel behind the form
 * reaches past it, and the panel's corner radius are configurable. Unset or
 * unusable values leave their variable unset, so the stylesheet's fallback
 * (the original look) applies. The values go into CSS, so alignment is taken
 * from a fixed list and the two sizes are parsed as numbers and clamped.
 */
export const LOGIN_TITLE_ALIGNS = ['left', 'center', 'right'] as const
/** 2026-10-01 (owner): the panel's distance from the screen edge is its own
 * setting, so a wide panel padding no longer pushes it to the edge. */
export const LOGIN_PANEL_MARGIN_MAX_PX = 48
export const LOGIN_PANEL_PADDING_MAX_PX = 24
export const LOGIN_PANEL_RADIUS_MAX_PX = 32

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

function pixels(value: string | undefined, max: number): string | undefined {
  if (value === undefined || value.trim() === '') return undefined
  const n = Number(value)
  if (!Number.isFinite(n)) return undefined
  return `${Math.min(Math.max(Math.round(n), 0), max)}px`
}

function titleAlign(value: string | undefined): string | undefined {
  return (LOGIN_TITLE_ALIGNS as readonly string[]).includes(value ?? '') ? value : undefined
}

export function loginThemeVars(theme: LiffTheme | undefined): CSSProperties {
  const panelEnabled = theme?.login_panel_enabled === 'true'
  const padding = pixels(theme?.login_panel_padding, LOGIN_PANEL_PADDING_MAX_PX)
  return {
    '--login-bg-color': theme?.login_bg_color ?? undefined,
    '--login-bg-image': theme?.login_bg_image ? `url(${theme.login_bg_image})` : undefined,
    '--login-button-color': theme?.button_color ?? undefined,
    '--login-panel-color': panelEnabled
      ? hexToRgba(theme?.login_panel_color ?? '#ffffff', Number(theme?.login_panel_opacity ?? '0.85'))
      : undefined,
    '--login-title-align': titleAlign(theme?.login_title_align),
    '--login-panel-margin': pixels(theme?.login_panel_margin, LOGIN_PANEL_MARGIN_MAX_PX),
    '--login-panel-pad-x': padding,
    '--login-panel-pad-y': padding,
    '--login-panel-radius': pixels(theme?.login_panel_radius, LOGIN_PANEL_RADIUS_MAX_PX),
  } as CSSProperties
}
